import { providerConfig } from "./providerConfig";

export type ProviderName = keyof typeof providerConfig;

function normalizedHost(host: string): string {
  return host.trim().toLowerCase();
}

/**
 * Guards an outbound call against `providerConfig`, which mirrors the processors
 * listed in the privacy policy. Call this before sending any personal data to a
 * third party, so that a provider we are not allowed to use fails loudly instead
 * of silently leaking data.
 *
 * `apiHost` pins the exact endpoint: several providers are only permitted on
 * their EU hosts (Deepgram, Google Cloud), so the host is part of what the
 * privacy policy promises, not an implementation detail.
 *
 * @param providerName Key in `providerConfig`.
 * @param apiHost Host the request will go to. Required when the provider has
 *   `hosts` configured, and must be omitted when it does not.
 * @returns The matched provider config entry, for callers that read further settings.
 * @throws If the provider is disabled, if `apiHost` is missing, unexpected, or
 *   not in the provider's configured hosts.
 */
export function assertProviderEnabled(
  providerName: ProviderName,
  apiHost?: string
) {
  const provider = providerConfig[providerName];
  if (!provider.enabled) {
    // Stop the request before any personal data is sent to a blocked provider.
    throw new Error(`Provider "${providerName}" is disabled by legal policy`);
  }

  const configuredHostEntries =
    "hosts" in provider &&
    Array.isArray(provider.hosts) &&
    provider.hosts.length > 0
      ? provider.hosts
      : null;

  if (configuredHostEntries) {
    const configuredHosts = configuredHostEntries.map((entry) =>
      normalizedHost(entry)
    );
    if (apiHost === undefined || normalizedHost(apiHost) === "") {
      throw new Error(
        `Provider "${providerName}" requires an apiHost that matches providerConfig.hosts`
      );
    }
    if (!configuredHosts.includes(normalizedHost(apiHost))) {
      throw new Error(
        `API host "${apiHost}" is not allowed for "${providerName}" (configured: ${configuredHostEntries.join(", ")})`
      );
    }
  } else if (apiHost !== undefined && normalizedHost(apiHost) !== "") {
    throw new Error(
      `Provider "${providerName}" has no hosts in providerConfig; omit apiHost`
    );
  }

  return provider;
}
